/**
 * mergeScannedLine — shared helper for adding a barcode-scanned
 * product to a line-items array. If the product is already on the
 * invoice the existing row's quantity is incremented; otherwise a new
 * row is appended.
 *
 * The math (line_total = qty × unit_price) matches what LineItemsGrid
 * does internally so the parent state remains consistent whether the
 * user added a product via combobox typing or via camera scanning.
 *
 * Returns the NEW line-items array (does not mutate).
 */
export const newUid = () => `li_${Math.random().toString(36).slice(2, 10)}`;

export function mergeScannedLine(currentLines, product, addQty) {
    const qtyDelta = Math.max(1, Math.floor(Number(addQty) || 0));
    const unit = Number(product.price ?? product.prices?.wholesale?.price ?? 0);
    const vat  = product.vat_rate != null ? Number(product.vat_rate) : 20;

    // Find an existing row already bound to this product (ignoring
    // blank rows and rows manually cleared).
    const idx = currentLines.findIndex((r) => r.product_id === product.id);

    if (idx >= 0) {
        const next = [...currentLines];
        const cur = next[idx];
        const curQty = Number(cur.quantity) || 0;
        const newQty = curQty + qtyDelta;
        next[idx] = {
            ...cur,
            quantity: newQty,
            line_total: +(newQty * (Number(cur.unit_price) || unit)).toFixed(2),
        };
        return next;
    }

    // If the LAST row is blank (no product_id), fill it instead of
    // appending — LineItemsGrid autoFocuses a blank row on mount.
    const last = currentLines[currentLines.length - 1];
    if (last && !last.product_id) {
        const next = [...currentLines];
        next[next.length - 1] = {
            ...last,
            product_id: product.id,
            product_name: product.name,
            sku: product.sku,
            unit_price: unit,
            vat_rate: vat,
            quantity: qtyDelta,
            batch_required: !!product.batch_required,
            batch_allocations: null,
            line_total: +(qtyDelta * unit).toFixed(2),
        };
        return next;
    }

    // Otherwise append a new row.
    return [
        ...currentLines,
        {
            _uid: newUid(),
            product_id: product.id,
            product_name: product.name,
            sku: product.sku,
            unit_price: unit,
            quantity: qtyDelta,
            vat_rate: vat,
            batch_required: !!product.batch_required,
            batch_allocations: null,
            line_total: +(qtyDelta * unit).toFixed(2),
        },
    ];
}

/**
 * unmergeScannedLine — reverse of `mergeScannedLine`. Given the same
 * (product, qty) tuple that was previously added, subtract that
 * quantity from the matching row. If the row's quantity drops to 0 or
 * below, remove the row entirely.
 *
 * Used by the "recent scans" strip in the barcode scanner overlay so
 * the operator can undo a mis-scan without leaving the camera.
 *
 * Returns the NEW line-items array (does not mutate).
 */
export function unmergeScannedLine(currentLines, product, undoQty) {
    const qty = Math.max(1, Math.floor(Number(undoQty) || 0));
    const idx = currentLines.findIndex((r) => r.product_id === product.id);
    if (idx < 0) return currentLines;

    const cur = currentLines[idx];
    const remaining = (Number(cur.quantity) || 0) - qty;

    if (remaining <= 0) {
        // Remove the row entirely.
        return currentLines.filter((_, i) => i !== idx);
    }
    const next = [...currentLines];
    next[idx] = {
        ...cur,
        quantity: remaining,
        line_total: +(remaining * (Number(cur.unit_price) || 0)).toFixed(2),
        batch_allocations: null, // trigger FEFO re-allocation
    };
    return next;
}
